import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environment/environment';
import { isDashboardShareLinkActive } from '../dashboard-share-link-api';
import { postDashboardData } from '../dashboard-data-api';
import { CateringApprovalStatus } from '../../../constants/catering-summary-labels';

export const CATERING_SUMMARY_DAY_OPTIONS = [1, 7, 14, 30] as const;
export const CATERING_SUMMARY_DEFAULT_DAYS = 14;

export type CateringSummaryDayOption = (typeof CATERING_SUMMARY_DAY_OPTIONS)[number];

export interface CateringSummaryExtraLine {
  extra_option_id: number;
  label: string;
  quantity: number;
  comment?: string;
}

export interface CateringSummaryEvent {
  event_id: number;
  guid: string;
  event_name: string;
  location: string;
  event_room?: string | null;
  catering_room_name?: string | null;
  start_date: string | null;
  end_date: string | null;
  start_time?: string | null;
  end_time?: string | null;
  customer_name?: string | null;
  catering_order_comment?: string | null;
  /** Deltagere til forplejning (inkl. instruktører; samme filter som præference-tælling). */
  participant_count: number;
  /** Kun status pending — bruges til godkendelsesflow, ikke som visningsantal. */
  current_pending_user_count?: number;
  approved_pending_user_count?: number | null;
  approved_at?: string | null;
  approval_status?: CateringApprovalStatus;
  preferences: Record<string, number>;
  extras: CateringSummaryExtraLine[];
}

export interface CateringSummaryDay {
  date: string;
  totals: {
    preferences: Record<string, number>;
    extras: CateringSummaryExtraLine[];
  };
  events: CateringSummaryEvent[];
}

export interface CateringSummaryData {
  period: { days: number; start: string; end: string };
  totals: {
    preferences: Record<string, number>;
    extras: CateringSummaryExtraLine[];
  };
  days: CateringSummaryDay[];
}

export function normalizeCateringSummaryDays(
  days: number | null | undefined
): CateringSummaryDayOption {
  return CATERING_SUMMARY_DAY_OPTIONS.includes(days as CateringSummaryDayOption)
    ? (days as CateringSummaryDayOption)
    : CATERING_SUMMARY_DEFAULT_DAYS;
}

function mapCateringSummaryPayload(
  body: unknown,
  safeDays: CateringSummaryDayOption
): CateringSummaryData {
  const payload =
    (body as { data?: CateringSummaryData })?.data ?? (body as CateringSummaryData);
  return {
    period: payload?.period ?? { days: safeDays, start: '', end: '' },
    totals: payload?.totals ?? { preferences: {}, extras: [] },
    days: payload?.days ?? [],
  };
}

export function getCateringSummaryData(
  http: HttpClient,
  days: number = CATERING_SUMMARY_DEFAULT_DAYS,
  cateringGroupId?: number | null
): Observable<CateringSummaryData> {
  const safeDays = normalizeCateringSummaryDays(days);
  let params = new HttpParams().set('filter_days', String(safeDays));
  if (cateringGroupId != null && cateringGroupId > 0) {
    params = params.set('filter_catering_group', String(cateringGroupId));
  }

  if (isDashboardShareLinkActive()) {
    const filters: Record<string, unknown> = { days: safeDays };
    if (cateringGroupId != null && cateringGroupId > 0) {
      filters['cateringGroupId'] = cateringGroupId;
    }
    return postDashboardData<{ data?: CateringSummaryData[] } | CateringSummaryData>(http, 'cateringSummary', {
      sourceType: 'cateringSummary',
      page: 1,
      pageSize: 1,
      filters,
    }).pipe(
      map((body) => {
        const listRow = (body as { data?: CateringSummaryData[] })?.data?.[0];
        const row = listRow ?? (body as CateringSummaryData);
        return mapCateringSummaryPayload(row, safeDays);
      }),
      catchError((error) => {
        console.error('[CATERING-SUMMARY] Failed to load catering summary via share link', error);
        return of({
          period: { days: safeDays, start: '', end: '' },
          totals: { preferences: {}, extras: [] },
          days: [],
        });
      })
    );
  }

  return http
    .get<CateringSummaryData | { data: CateringSummaryData }>(
      `${environment.apiUrl}/dashboard/catering-summary`,
      { params }
    )
    .pipe(
      map((body) => mapCateringSummaryPayload(body, safeDays)),
      catchError((error) => {
        console.error('[CATERING-SUMMARY] Failed to load catering summary', error);
        return of({
          period: { days: safeDays, start: '', end: '' },
          totals: { preferences: {}, extras: [] },
          days: [],
        });
      })
    );
}

/**
 * Public unique-link summary (no auth). Uses fetch to avoid auth interceptor blocking.
 */
export async function fetchCateringSummaryForUniqueLink(
  linkUuid: string,
  days: number = CATERING_SUMMARY_DEFAULT_DAYS
): Promise<CateringSummaryData> {
  const safeDays = normalizeCateringSummaryDays(days);
  const empty: CateringSummaryData = {
    period: { days: safeDays, start: '', end: '' },
    totals: { preferences: {}, extras: [] },
    days: [],
  };

  if (!linkUuid) {
    return empty;
  }

  const qs = new URLSearchParams({ filter_days: String(safeDays) });
  const url = `${
    environment.apiUrl
  }/events/catering/unique-links/${encodeURIComponent(linkUuid)}/summary?${qs.toString()}`;

  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'omit',
    });

    const bodyText = await res.text();
    if (!res.ok) {
      console.error('[CATERING-SUMMARY] Unique link summary failed', res.status, bodyText);
      return empty;
    }

    let parsed: unknown = null;
    try {
      parsed = JSON.parse(bodyText);
    } catch {
      const match = bodyText.match(/\{[\s\S]*\}\s*$/);
      if (match?.[0]) {
        parsed = JSON.parse(match[0]);
      }
    }

    return mapCateringSummaryPayload(parsed, safeDays);
  } catch (error) {
    console.error('[CATERING-SUMMARY] Unique link summary fetch error', error);
    return empty;
  }
}
